import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import RenderComponentLoader from "../../../components/StayUpToDateComponent/StayUpToDateRenderComponent/RenderComponentLoader";
import { useLoadRenderComponent } from "../../../components/StayUpToDateComponent/StayUpToDateRenderComponent/useLoadRenderComponent";
import { TemplateFeedDesignBoundary } from "./TemplateFeedDesignBoundary";
import { WEBSITE_TEMPLATE_SURFACE } from "../../../Utils/StayUpToDate/componentSurface";
import { supabase } from "../../../config/supabaseClient";
import { isNavigable } from "../../../Utils/StayUpToDate/RenderComponent";
import { updateRefClick } from "../../../Utils/SupabaseServices/ContentDatabase";
import { useTemplateAnalyticsOptional } from "./TemplateAnalyticsContext";
import { useTemplateFeedPostTracking } from "./useTemplateFeedPostTracking";
import { PostClickTrackingProvider } from "../../../components/FeedPost/PostInfo/PostClickTrackingContext";
import { TemplateFeedMediaRevealRoot } from "./TemplateFeedMediaRevealRoot";
import type { ThemeTokens } from "./types";
import "./templateStayUpToDateEmbed.css";
import "./templateSectionGridLoader.css";
import "./template.css";
/* Mute / volume icon positioning for hover-play tiles (same as legacy LiB). */
import "../../../components/CreatorFeed/CreatorFeed.css";
import { TemplateThemeRoot } from "./TemplateThemeRoot";
import { tplThemeVars } from "./templateThemeVars";
import { useTemplateStyles } from "./useTemplateStyles";
import {
  isTemplateTabFeedReady,
  markTemplateTabFeedReady,
} from "./templateTabFeedReadyCache";
import { TemplateTabFeedPendingGrid } from "./TemplateTabFeedPendingGrid";

export { TemplateTabFeedPendingGrid };

/** Page-specific grid tokens for collection feeds (shop ≠ content). */
function templateFeedGridStyle(
  pageVariant: "shop" | "content",
  gridColumns?: number,
): React.CSSProperties | undefined {
  const cols =
    typeof gridColumns === "number" && Number.isFinite(gridColumns)
      ? Math.max(2, Math.min(6, Math.round(gridColumns)))
      : undefined;

  // Product catalog: masonry columns only — media is natural height ≤ 350px (no aspect lock).
  if (pageVariant === "shop") {
    if (cols == null) return undefined;
    return {
      ["--tpl-grid-cols" as string]: String(cols),
      ["--tpl-grid-gap" as string]: "14px",
    } as React.CSSProperties;
  }

  // Content catalog: columns + 3/4 frame (same default as `.mte-tpl-shop-card-frame`).
  const ratio = "3 / 4";
  return {
    ...(cols != null
      ? { ["--tpl-grid-cols" as string]: String(cols) }
      : null),
    ["--tpl-grid-gap" as string]: "14px",
    ["--content-rectangle-aspect" as string]: ratio,
    ["--tpl-card-aspect" as string]: ratio,
    ["--feed-post-aspect" as string]: ratio,
  } as React.CSSProperties;
}

const FEED_TILE_SELECTOR =
  ".mte-tpl-grid-tile, .item-grid > *, .links-grid-container-reels > .link-url, .links-grid-container-reels > a, .wrapper-div .product-card, .tpl-stayuptodate-embed .product-card";

function slugifyTabSlug(label: string): string {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function stayRowMatchesSlug(
  row: Record<string, unknown> | undefined,
  slug: string,
): boolean {
  const want = slug.trim().toLowerCase();
  if (!want || !row) return false;
  const rn = String(row.route_name ?? "").trim().toLowerCase();
  const uid = String(row.unique_id ?? "").trim().toLowerCase();
  const nm = String(row.name ?? "").trim().toLowerCase();
  const slugName = slugifyTabSlug(String(row.name ?? ""));
  return rn === want || uid === want || nm === want || slugName === want;
}

function countFeedTiles(root: HTMLElement | null): number {
  if (!root) return 0;
  return root.querySelectorAll(FEED_TILE_SELECTOR).length;
}

export interface TemplateStayUpToDateTabPanelProps {
  userId: string;
  username: string;
  renderSlug: string;
  T: ThemeTokens;
  pageVariant: "shop" | "content";
  /** Called when the active tab’s feed finishes loading with zero items — parent may hide the tab. */
  onTabFeedResolved?: (slug: string, hasItems: boolean) => void;
  maxItems?: number;
  aspect?: string;
  showShopButton?: boolean;
  buttonLabel?: string;
  /** Shop/content grid column override (3–5) from page inspector. */
  gridColumns?: number;
  /**
   * `grid` — shop/content tab skeleton (default).
   * `none` — no feed grid ghost (homepage newsletter embed).
   */
  feedPending?: "grid" | "none";
  contentView?: "latest" | "series";
  contentTabLabel?: string;
  /**
   * Omit for website Latest tabs (`template_components`).
   * Pass `null` for LiB `stayuptodate_components` (collection deep-links).
   */
  componentFrom?: string | null;
  /** Fires once when this tab’s feed is ready to paint (tiles present or settled empty). */
  onFeedPaintReady?: () => void;
}

/**
 * Loads one stayuptodate (or embed) row like link-in-bio, then renders it with
 * {@link RenderComponentLoader} + {@link RenderIndividualUI} (same infinite UX as /:username/:id).
 */
export function TemplateStayUpToDateTabPanel({
  userId,
  username,
  renderSlug,
  T,
  pageVariant,
  onTabFeedResolved,
  gridColumns,
  maxItems,
  feedPending = "grid",
  contentView,
  contentTabLabel,
  onFeedPaintReady,
  componentFrom,
}: TemplateStayUpToDateTabPanelProps) {
  const rowFrom =
    componentFrom === null
      ? undefined
      : (componentFrom ?? WEBSITE_TEMPLATE_SURFACE);
  const { data, isLoading, isFetching, isError } = useLoadRenderComponent(
    userId,
    renderSlug,
    rowFrom,
    supabase,
    componentFrom === null ? username : undefined,
  );

  const slugTrim = renderSlug.trim();
  const cachedFeedReady = isTemplateTabFeedReady(userId, slugTrim);

  const [feedReady, setFeedReady] = useState(cachedFeedReady);
  const [ghostVisible, setGhostVisible] = useState(!cachedFeedReady);
  const [ghostExiting, setGhostExiting] = useState(false);
  /** Enter stagger only on first reveal; remove so later shows stay instant. */
  const [playEnterAnim, setPlayEnterAnim] = useState(!cachedFeedReady);
  const emptyFeedRef = useRef(false);
  const embedNodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isTemplateTabFeedReady(userId, slugTrim)) {
      setFeedReady(true);
      setGhostVisible(false);
      setGhostExiting(false);
      setPlayEnterAnim(false);
      emptyFeedRef.current = false;
      return;
    }
    setFeedReady(false);
    setGhostVisible(true);
    setGhostExiting(false);
    setPlayEnterAnim(true);
    emptyFeedRef.current = false;
  }, [renderSlug, userId, slugTrim]);

  useEffect(() => {
    if (!feedReady) {
      setGhostVisible(true);
      setGhostExiting(false);
      return;
    }
    // Drop the skeleton immediately so tab switches / first paint aren't held
    // behind a ~340ms exit fade. A short optional fade stays for polish only.
    setGhostExiting(true);
    const t = window.setTimeout(() => {
      setGhostVisible(false);
      setGhostExiting(false);
    }, 80);
    return () => window.clearTimeout(t);
  }, [feedReady]);

  useEffect(() => {
    if (!feedReady || !playEnterAnim) return;
    const t = window.setTimeout(() => setPlayEnterAnim(false), 700);
    return () => window.clearTimeout(t);
  }, [feedReady, playEnterAnim]);

  useEffect(() => {
    if (!feedReady) return;
    // Empty tabs are removed by the parent — don't lift the boot overlay for them.
    if (emptyFeedRef.current) return;
    onFeedPaintReady?.();
  }, [feedReady, onFeedPaintReady]);

  const templateAnalytics = useTemplateAnalyticsOptional();
  const trackingPageKey =
    templateAnalytics?.pageKey ||
    (pageVariant === "content" ? "content" : "shop");
  const postTracking = useTemplateFeedPostTracking(trackingPageKey, {
    sectionId: pageVariant === "content" ? "contentGrid" : "productGrid",
    sectionType: pageVariant === "content" ? "ContentGrid" : "ProductGrid",
    carouselSlug: renderSlug,
    contentView: pageVariant === "content" ? contentView : undefined,
    contentTab:
      pageVariant === "content" ? contentTabLabel || renderSlug : undefined,
  });

  const handleRedirect = useCallback(
    (
      postId: string,
      media_url?: string,
      platform?: string,
      name?: string,
      title?: string,
      isLinks?: boolean,
    ) => {
      if (!userId || !postId) return;
      const label = name || title || postId;
      if (postTracking) {
        if (isLinks === true) {
          postTracking.handleShop?.(
            postId,
            media_url || "",
            platform,
            label,
            userId,
          );
        } else if (isLinks === false) {
          postTracking.handleRedirect?.(
            postId,
            media_url || "",
            platform,
            label,
            userId,
          );
        } else {
          templateAnalytics?.trackPostClick({
            postId,
            mediaUrl: media_url,
            platform,
            name,
            title,
            sectionId: pageVariant === "content" ? "contentGrid" : "productGrid",
            sectionType:
              pageVariant === "content" ? "ContentGrid" : "ProductGrid",
            carouselSlug: renderSlug,
            contentView: pageVariant === "content" ? contentView : undefined,
            contentTab:
              pageVariant === "content"
                ? contentTabLabel || renderSlug
                : undefined,
          });
          void updateRefClick(postId, userId);
        }
        return;
      }
      templateAnalytics?.trackPostClick({
        postId,
        mediaUrl: media_url,
        platform,
        name,
        title,
        sectionId: pageVariant === "content" ? "contentGrid" : "productGrid",
        sectionType: pageVariant === "content" ? "ContentGrid" : "ProductGrid",
        carouselSlug: renderSlug,
        contentView: pageVariant === "content" ? contentView : undefined,
        contentTab:
          pageVariant === "content" ? contentTabLabel || renderSlug : undefined,
      });
      void updateRefClick(postId, userId);
    },
    [
      userId,
      templateAnalytics,
      postTracking,
      pageVariant,
      renderSlug,
      contentView,
      contentTabLabel,
    ],
  );

  const onFeedResolved = useCallback(
    (hasItems: boolean) => {
      const slug = String(renderSlug || "").trim();
      if (slug) onTabFeedResolved?.(slug, hasItems);
      if (!hasItems) {
        emptyFeedRef.current = true;
        setFeedReady(true);
        markTemplateTabFeedReady(userId, slugTrim);
      }
    },
    [renderSlug, onTabFeedResolved, userId, slugTrim],
  );

  const row = data as Record<string, unknown> | undefined;
  const componentId = String(
    (row?.route_name as string)?.trim() ||
      (row?.unique_id as string)?.trim() ||
      renderSlug,
  );

  const cssVars = useMemo(
    () =>
      ({
        ["--tpl-grid-gap" as string]: "clamp(10px, 2cqi, 18px)",
        ["--tpl-radius" as string]: T.radius,
        ["--tpl-card-border" as string]: `${T.ink}22`,
        ["--tpl-surface" as string]: T.surface,
        ["--tpl-ink" as string]: T.ink,
        ["--tpl-muted" as string]: T.muted,
      }) as React.CSSProperties,
    [T.device, T.radius, T.surface, T.ink, T.muted],
  );

  const embedStyle = useMemo(
    () => ({ ...cssVars, ...tplThemeVars(T) }) as React.CSSProperties,
    [cssVars, T],
  );
  const embedStyleRef = useTemplateStyles<HTMLDivElement>(embedStyle);
  const embedRef = useCallback(
    (node: HTMLDivElement | null) => {
      embedNodeRef.current = node;
      embedStyleRef(node);
    },
    [embedStyleRef],
  );

  const rowOk = stayRowMatchesSlug(row, slugTrim);
  const rowQueryLoading =
    isLoading || (isFetching && data === undefined);
  const rowPending =
    !slugTrim || !rowOk || rowQueryLoading;

  useLayoutEffect(() => {
    if (!slugTrim || rowPending || !row || emptyFeedRef.current) return;

    const root = embedNodeRef.current;
    if (!root) return;

    const markReady = () => {
      markTemplateTabFeedReady(userId, slugTrim);
      setFeedReady(true);
    };

    if (countFeedTiles(root) > 0) {
      markReady();
      return;
    }

    let cancelled = false;
    const reveal = () => {
      if (cancelled) return;
      if (countFeedTiles(root) > 0) {
        markReady();
        return true;
      }
      return false;
    };

    const observer = new MutationObserver(() => {
      if (reveal()) observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true });

    const fallback = window.setTimeout(() => {
      if (!cancelled) markReady();
    }, 10000);

    return () => {
      cancelled = true;
      observer.disconnect();
      window.clearTimeout(fallback);
    };
  }, [slugTrim, rowPending, row, renderSlug, rowOk, userId]);

  if (!slugTrim) return null;

  const feedGridStyle = templateFeedGridStyle(pageVariant, gridColumns);

  if (rowPending) {
    if (feedPending === "none") return null;
    return (
      <div
        className={`mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--${pageVariant}`}
        style={feedGridStyle}
        aria-busy="true"
      >
        <TemplateTabFeedPendingGrid
          pageVariant={pageVariant}
          device={T.device}
          maxItems={maxItems}
          T={T}
        />
      </div>
    );
  }

  if (isError || !row) {
    return (
      <TemplateThemeRoot T={T} className="mte-sut-error">
        This section could not be loaded.
      </TemplateThemeRoot>
    );
  }

  const showGhost = ghostVisible;
  const showFeed = feedReady;
  const rowType = String(
    (row as any)?.type || (row as any)?.display_name || renderSlug || "",
  )
    .trim()
    .toLowerCase();
  const isYouTubeEmbed =
    pageVariant === "content" &&
    (rowType === "youtube" ||
      rowType.includes("youtube") ||
      String(contentTabLabel || "")
        .toLowerCase()
        .includes("youtube"));

  return (
    <div
      className={`mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--${pageVariant}`}
      style={feedGridStyle}
      aria-busy={!showFeed}
    >
      {showGhost && feedPending !== "none" ? (
        <TemplateTabFeedPendingGrid
          key={renderSlug}
          pageVariant={pageVariant}
          device={T.device}
          maxItems={maxItems}
          exiting={ghostExiting}
          T={T}
        />
      ) : null}
      <div
        ref={embedRef}
        className={[
          "tpl-stayuptodate-embed",
          "tpl-template-feed-grid",
          `tpl-template-feed-grid--${pageVariant}`,
          isYouTubeEmbed ? "tpl-template-feed-grid--youtube" : "",
          "mte-sut-embed-wrap",
          showFeed
            ? playEnterAnim
              ? "mte-tpl-grid-enter"
              : ""
            : "mte-tpl-feed-mounting",
        ]
          .filter(Boolean)
          .join(" ")}
        data-platform={isYouTubeEmbed ? "youtube" : undefined}
        style={feedGridStyle}
      >
        <TemplateFeedDesignBoundary T={T}>
          <TemplateFeedMediaRevealRoot>
          <PostClickTrackingProvider value={postTracking || {}}>
          <RenderComponentLoader
            componentId={componentId}
            user_id={userId}
            username={username}
            brand_color={T.accent}
            handleRedirect={handleRedirect}
            isHandleNavigate={isNavigable(row)}
            data={row}
            from={
              componentFrom === null ? "landing-page" : "website-template"
            }
            individualPostLayout
            templatePageVariant={pageVariant}
            templateDevice={T.device}
            isMobileView={T.device === "mobile"}
            templateGridColumns={gridColumns}
            onFeedResolved={onFeedResolved}
          />
          </PostClickTrackingProvider>
          </TemplateFeedMediaRevealRoot>
        </TemplateFeedDesignBoundary>
      </div>
    </div>
  );
}
